import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Mail, Send, LifeBuoy } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, LangToggle } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact_sent_toast"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contact_fail"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 glass-nav border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <Gift size={20} className="text-white" />
            </div>
            <span className="font-heading font-bold text-lg">hediyeniiste</span>
          </Link>
          <LangToggle />
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md fade-up" data-testid="contact-page">
          <div className="bg-card rounded-2xl border border-border/50 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-8">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-success">
                <p className="text-4xl mb-4">💌</p>
                <h1 className="font-heading text-xl font-bold mb-2">{t("contact_success_t")}</h1>
                <p className="text-sm text-muted-foreground mb-6">{t("contact_success_d")}</p>
                <Link to="/" className="text-primary font-semibold text-sm" data-testid="contact-back-home">{t("back_home")}</Link>
              </div>
            ) : (
              <>
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Mail size={20} className="text-primary" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-1">{t("support_title")}</h1>
                <p className="text-sm text-muted-foreground mb-6">{t("support_sub")}</p>
                <div className="mb-6" data-testid="support-quick-help">
                  <p className="font-heading font-semibold text-sm mb-2 flex items-center gap-1.5"><LifeBuoy size={15} className="text-primary" /> {t("sup_h")}</p>
                  <Accordion type="single" collapsible className="space-y-2">
                    {["sup1", "sup2", "sup3"].map((k) => (
                      <AccordionItem key={k} value={k} className="bg-secondary/50 rounded-xl px-4 border-0" data-testid={`support-faq-${k}`}>
                        <AccordionTrigger className="text-sm font-medium text-left hover:no-underline py-3">{t(`${k}_q`)}</AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground">{t(`${k}_a`)}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="c-name">{t("lbl_your_name")}</Label>
                    <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 rounded-xl h-11" placeholder="Kemal Yılmaz" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="c-email">{t("email_label")}</Label>
                    <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 rounded-xl h-11" placeholder="ornek@mail.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="c-msg">{t("lbl_message")}</Label>
                    <Textarea id="c-msg" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="mt-1 rounded-xl" rows={5} maxLength={2000} placeholder="Merhaba..." data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full rounded-full h-11 font-semibold active:scale-95 transition-transform" data-testid="contact-submit-btn">
                    {busy ? t("sending") : <><Send size={15} className="mr-1.5" /> {t("aria_send")}</>}
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground text-center mt-4">
                  {t("or_direct")} <a href="mailto:destek@hediyeniiste.com" className="text-primary">destek@hediyeniiste.com</a>
                </p>
                <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP}`} target="_blank" rel="noopener noreferrer"
                  className="mt-4 w-full rounded-full h-11 font-semibold bg-[#25D366] text-white flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  data-testid="contact-whatsapp-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  {t("whatsapp_support")}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
